import { nextReadyOp, parkConflict } from './dependencies';
import type { SyncSnapshot } from '../../../shared/types';
import { ApiError, NetworkError, UNAUTHORIZED_EVENT, apiFetch, getApiKey } from './api';
import { getDb } from './db';
import { countByStatus, markFailed, noteAttempt, removeOp } from './outbox';
import { keepExportProfile } from './activity';
import { keepPlaybookStamp, playbookStamp } from './store';
import { applyRoutePutResult, applyServerRoutes, keepRouteSettings } from './dayRoutes';
import { replaceMirrorIfIdle, storeEvents } from './store';

/**
 * Sync engine (D3): flush the outbox FIFO, then pull a full snapshot and replace the mirror.
 * Single-flight, debounced kicks, exponential backoff on transient failures.
 */
export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'unauthorized';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  failed: number;
  parked?: number;
  lastSync: string | null;
  message?: string;
}

export const syncEvents = new EventTarget();

let status: SyncStatus = { state: 'idle', pending: 0, failed: 0, lastSync: null };
export const getSyncStatus = (): SyncStatus => status;

export const onSyncStatus = (cb: (s: SyncStatus) => void): (() => void) => {
  const handler = () => cb(status);
  syncEvents.addEventListener('status', handler);
  return () => syncEvents.removeEventListener('status', handler);
};

const setStatus = (patch: Partial<SyncStatus>) => {
  status = { ...status, ...patch };
  syncEvents.dispatchEvent(new Event('status'));
};

const LAST_SYNC_KEY = 'last_sync';
const readLastSync = async (): Promise<string | null> => {
  const row = await (await getDb()).get('meta', LAST_SYNC_KEY);
  return typeof row?.value === 'string' ? row.value : null;
};
const writeLastSync = async (iso: string) => (await getDb()).put('meta', { key: LAST_SYNC_KEY, value: iso });

type FlushResult =
  { stopped: false } | { stopped: true; reason: 'network' | 'server' | 'unauthorized'; message: string };

/** Not a verdict on the op's body: a 5xx, a rate limit or timeout, or a 2xx that is not the API
 *  envelope (a build with the wrong VITE_API_URL gets the app's own HTML back). Retry later. */
const isTransient = (err: ApiError): boolean =>
  err.status >= 500 ||
  err.status === 408 ||
  err.status === 425 ||
  err.status === 429 ||
  err.code === 'BAD_RESPONSE';

/** What the Sync screen shows for a parked op: the server's field details when it sent them. */
const rejectionText = (err: ApiError): string => {
  const details = err.details?.map((d) => `${d.path}: ${d.message}`).join('; ');
  return `${err.code}: ${details || err.message}`;
};

/** Replay queued mutations in order. Stops on the first transient failure; parks permanent ones. */
export const flushOutbox = async (): Promise<FlushResult> => {
  for (;;) {
    const op = await nextReadyOp();
    if (!op) break;
    const seq = op.seq as number;
    try {
      const result = await apiFetch(op.method, op.path, op.body);
      applyRoutePutResult(op, result);
      await removeOp(seq);
    } catch (err) {
      if (err instanceof NetworkError) {
        await noteAttempt(seq, op.attempts + 1, err.message);
        return { stopped: true, reason: 'network', message: err.message };
      }
      if (err instanceof ApiError) {
        if (err.status === 401) return { stopped: true, reason: 'unauthorized', message: err.message };
        if (op.method === 'DELETE' && err.status === 404) {
          await removeOp(seq); // already gone server-side: success for our purposes
          continue;
        }
        if (isTransient(err)) {
          await noteAttempt(seq, op.attempts + 1, `${err.status} ${err.message}`);
          return { stopped: true, reason: 'server', message: `${err.status} ${err.message}` };
        }
        if (err.status === 409 && err.existing_lead_id) {
          await parkConflict(seq, err.existing_lead_id);
          continue;
        }
        await markFailed(seq, op.attempts + 1, rejectionText(err)); // a real 4xx verdict: park and move on
        continue;
      }
      const message = err instanceof Error ? err.message : String(err);
      await noteAttempt(seq, op.attempts + 1, message);
      return { stopped: true, reason: 'server', message };
    }
  }
  return { stopped: false };
};

let running: Promise<SyncStatus> | null = null;
let rerunRequested = false;
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let backoffMs = 5_000;
const MAX_BACKOFF = 120_000;

const scheduleRetry = () => {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => void runSync(), backoffMs);
  backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF);
};

const syncOnce = async (): Promise<SyncStatus> => {
  const counts = await countByStatus();
  if (!getApiKey()) {
    setStatus({ state: 'unauthorized', ...counts });
    return status;
  }
  // navigator.onLine is not trusted: Chrome on a Mac can say offline while connected (a VPN or a
  // virtual network adapter), and the mirror then never filled. A request that fails says offline.
  setStatus({ state: 'syncing', ...counts, message: undefined });

  const flush = await flushOutbox();
  if (flush.stopped) {
    const after = await countByStatus();
    if (flush.reason === 'unauthorized') {
      setStatus({ state: 'unauthorized', ...after, message: flush.message });
    } else {
      setStatus({
        state: flush.reason === 'network' ? 'offline' : 'error',
        ...after,
        message: flush.message,
      });
      scheduleRetry();
    }
    return status;
  }

  try {
    // the device says which copy of the message library it has, so the server can leave the rows
    // out when nothing changed (D88) — 50-odd long messages on every app open otherwise
    const have = await playbookStamp();
    const snap = await apiFetch<SyncSnapshot>(
      'GET',
      `/api/sync${have ? `?playbook=${encodeURIComponent(have)}` : ''}`,
    );
    // A local write may have landed while the pull was in flight. Its rows are only in the mirror
    // + outbox, so replacing the mirror now would make it vanish from the UI until the next sync.
    // replaceMirrorIfIdle checks the outbox INSIDE the replacement transaction (writes are also
    // single-transaction), so the race is closed; on skip we run again (rerunRequested).
    const replaced = await replaceMirrorIfIdle(snap);
    if (!replaced) {
      rerunRequested = true;
    } else {
      // the day routes and Plan tomorrow's settings (D84); an older API sends neither: keep ours
      if (snap.day_routes) await applyServerRoutes(snap.day_routes);
      if (snap.route_settings) await keepRouteSettings(snap.route_settings);
      // the Salesforce mapping (D91); an older API sends none: the copy on the device stays
      if (snap.export_profile) await keepExportProfile(snap.export_profile);
      // the stamp is kept only once the rows it names are in the mirror
      if (snap.playbook_stamp !== undefined && (snap.playbook || snap.playbook_stamp === have))
        await keepPlaybookStamp(snap.playbook_stamp);
      const lastSync = new Date().toISOString();
      await writeLastSync(lastSync);
      setStatus({ lastSync });
    }
    backoffMs = 5_000;
    clearTimeout(retryTimer);
    setStatus({ state: 'idle', ...(await countByStatus()), message: undefined });
  } catch (err) {
    const after = await countByStatus();
    if (err instanceof ApiError && err.status === 401) {
      setStatus({ state: 'unauthorized', ...after, message: err.message });
    } else {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ state: err instanceof NetworkError ? 'offline' : 'error', ...after, message });
      scheduleRetry();
    }
  }
  return status;
};

/** One run that always ends in a terminal status: a failure of the local database itself (storage
 *  pressure, a connection closed by another tab) must not leave the pill on "Syncing…" or reject
 *  the promise that `void runSync()` callers never observe. */
const doSync = async (): Promise<SyncStatus> => {
  try {
    return await syncOnce();
  } catch (err) {
    setStatus({ state: 'error', message: err instanceof Error ? err.message : String(err) });
    scheduleRetry();
    return status;
  }
};

/** Run a sync now. Single-flight: a call during an in-flight run shares it AND schedules one more
 *  run right after, so writes made mid-sync are never left waiting for the next trigger. */
export const runSync = (): Promise<SyncStatus> => {
  if (running) {
    rerunRequested = true;
    return running;
  }
  running = doSync().finally(() => {
    running = null;
    if (rerunRequested) {
      rerunRequested = false;
      void runSync();
    }
  });
  return running;
};

/** Sync now and also wait for the follow-up run a mid-run write schedules, so every write made
 *  before this call has been pushed (or is stuck behind an error) when it resolves. Use before a
 *  server-side action that references rows created on the phone (drafts, feature launch). */
export const syncAndSettle = async (): Promise<SyncStatus> => {
  let result = await runSync();
  while (running) result = await running;
  return result;
};

/** Wait for a run already in flight — without asking for another: a caller with nothing of its own
 *  to push (the contract drafts at app open) must not double the boot pull. */
export const joinSync = async (): Promise<void> => {
  if (running) await running;
};

/** Debounced sync request (used after local writes). */
export const kickSync = (delayMs = 400): void => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => void runSync(), delayMs);
};

/** Refresh counters/lastSync into status without hitting the network (e.g. on boot). */
export const refreshSyncStatus = async (): Promise<SyncStatus> => {
  const [counts, lastSync] = await Promise.all([countByStatus(), readLastSync()]);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  setStatus({
    ...counts,
    lastSync,
    state: offline ? 'offline' : status.state === 'syncing' ? 'syncing' : status.state,
  });
  return status;
};

/** Wire browser + store triggers. Returns a cleanup function. */
export const installSyncTriggers = (): (() => void) => {
  // the pill gets the state with the outbox counts, or without them when the local database cannot be read
  const setState = (state: SyncState) =>
    countByStatus().then(
      (c) => setStatus({ state, ...c }),
      () => setStatus({ state }),
    );
  const onOnline = () => kickSync(0);
  const onOffline = () => void setState('offline');
  const onVisible = () => {
    if (document.visibilityState === 'visible') kickSync(0);
  };
  const onWrite = () => kickSync(400);
  const onUnauthorized = () => void setState('unauthorized');
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);
  storeEvents.addEventListener('write', onWrite);
  window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    storeEvents.removeEventListener('write', onWrite);
    window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    clearTimeout(debounceTimer);
    clearTimeout(retryTimer);
  };
};

/** Tests only. */
export const _resetSyncForTests = (): void => {
  clearTimeout(debounceTimer);
  clearTimeout(retryTimer);
  running = null;
  rerunRequested = false;
  backoffMs = 5_000;
  status = { state: 'idle', pending: 0, failed: 0, lastSync: null };
};
